import { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { RATypography } from "@/components/customs";
import { RAImage } from "@/components/customs";
import { Row, Col, Grid } from "antd";
import { PATH_PUBLIC_IMAGES } from "@/config/images";

import { useTrackVisibility } from "react-intersection-observer-hook";
const { useBreakpoint } = Grid;

const CardSectionItem = ({ noId, title = "", description = "", divider = false }) => {
	return (
		<WrapperItem divider={divider}>
			<Row style={{ flexWrap: "nowrap" }}>
				<Col style={{ marginRight: 16 }}>
					<NoText>{noId}</NoText>
				</Col>
				<Col>
					<RATypography variant="sub-heading4">{title}</RATypography>
					<RATypography variant="body3" top="4px" color="#595959">
						{description}
					</RATypography>
				</Col>
			</Row>
		</WrapperItem>
	);
};

export function HomeUtilities({ why_fly_private = {}, fullHeight = false }) {
	const [ref, { isVisible }] = useTrackVisibility();
	const screens = useBreakpoint();
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (isVisible) {
			setVisible(true);
		}
	}, [isVisible]);

	return (
		<HomeUtilitiesContainer fullheight={fullHeight.toString()} ref={ref}>
			<EachCol xxl={12} xl={12} lg={12} md={24} sm={24} xs={24} className={`image-column`}>
				<ImageContainer>
					<EachImage>
						<RAImage
							url={why_fly_private?.photos?.photo1 || PATH_PUBLIC_IMAGES.home_utilities_1}
							width="100%"
							height="100%"
							borderRadius="12px"
							className="image-left"
						/>
					</EachImage>
					<EachImage>
						<RAImage
							url={why_fly_private?.photos?.photo2 || PATH_PUBLIC_IMAGES.home_utilities_2}
							width="100%"
							height="100%"
							borderRadius="12px"
							className="image-top-right"
						/>
					</EachImage>
					<EachImage>
						<RAImage
							url={why_fly_private?.photos?.photo3 || PATH_PUBLIC_IMAGES.home_utilities_3}
							width="100%"
							height="100%"
							borderRadius="12px"
							className="image-bottom-right"
						/>
					</EachImage>
				</ImageContainer>
			</EachCol>
			<EachCol className={`introduction-column`} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
				<WrapperContent>
					<RATypography variant={screens?.md ? "h2" : "h4"} whiteSpace="pre-line">
						{why_fly_private?.title || ""}
					</RATypography>
				</WrapperContent>
				<div>
					<RATypography variant="body3" top="12px" color="#595959" whiteSpace="pre-line" maxLines={3}>
						{why_fly_private?.sub_title || ""}
					</RATypography>
				</div>
				<WrapperSection>
					<div className={`${visible && "animate__animated animate__fadeInDown"}`}>
						<CardSectionItem
							noId="01"
							divider
							title={why_fly_private?.hight_light1?.title || ""}
							description={why_fly_private?.hight_light1?.description || ""}
						/>
					</div>
					<div
						className={`${visible && "animate__animated animate__fadeInDown"}`}
						style={{ animationDuration: "1.5s" }}
					>
						<CardSectionItem
							noId="02"
							divider
							title={why_fly_private?.hight_light2?.title || ""}
							description={why_fly_private?.hight_light2?.description || ""}
						/>
					</div>
					<div
						className={`${visible && "animate__animated animate__fadeInDown"}`}
						style={{ animationDuration: "2s" }}
					>
						<CardSectionItem
							noId="03"
							divider
							title={why_fly_private?.hight_light3?.title || ""}
							description={why_fly_private?.hight_light3?.description || ""}
						/>
					</div>
					<div
						className={`${visible && "animate__animated animate__fadeInDown"}`}
						style={{ animationDuration: "2.5s" }}
					>
						<CardSectionItem
							noId="04"
							title={why_fly_private?.hight_light4?.title || ""}
							description={why_fly_private?.hight_light4?.description || ""}
						/>
					</div>
				</WrapperSection>
			</EachCol>
		</HomeUtilitiesContainer>
	);
}

const ImageContainer = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: 100px 100px 100px 100px 100px;
	grid-template-rows: 300px 200px;
	gap: 8px;
	@media (max-width: 1600px) {
		grid-template-columns: 90px 90px 90px 90px 90px;
		grid-template-rows: 300px 200px;
	}
	@media (max-width: 1199px) {
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
		grid-template-rows: 276px 184px;
	}
	@media (max-width: 1199px) {
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
		grid-template-rows: 276px 184px;
	}
	@media (max-width: 991px) {
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
		grid-template-rows: 320px 160px;
	}
	@media (max-width: 767px) {
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
		grid-template-rows: 260px 122px;
	}
	@media (max-width: 575px) {
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
		grid-template-rows: 220px 90px;
	}
`;

const EachImage = styled.div`
	&:first-child {
		grid-column: 1/4;
		grid-row: 1/3;
	}
	&:nth-child(2) {
		grid-column: 4/6;
		grid-row: 1/2;
	}
	&:nth-child(3) {
		grid-column: 4/6;
		grid-row: 2/3;
	}
`;

const HomeUtilitiesContainer = styled(Row)`
	width: 100%;
	height: ${(props) => (props.fullheight === "true" ? "100vh" : "auto")};
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	padding: 84px 0;
	.image-column {
		padding-right: 30px;
	}
	.introduction-column {
		padding-left: 30px;
	}

	@media (max-width: 992px) {
		padding: 64px 0;
		.image-column {
			padding-right: 0;
		}
		.introduction-column {
			padding-left: 0;
		}
	}
	@media (max-width: 767px) {
		padding: 44px 0;
	}
	@media (max-width: 575px) {
		padding: 24px 0;
	}
`;

const EachCol = styled(Col)`
	padding-top: 20px;
`;

const WrapperContent = styled.div`
	padding: 4px 12px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperSection = styled.div`
	padding: 12px 0px;
	display: flex;
	flex-direction: column;
`;
const WrapperItem = styled.div`
	padding: 16px 0px;
	gap: 16px;
	min-height: 50px;

	${(props) =>
		props.divider &&
		css`
			border-bottom: 1px solid #e9e8e8;
		`}

	@media (max-width: 991px) {
		padding: 8px 0;
	}
`;
const NoText = styled.div`
	font-family: "Bebas Neue";
	font-style: normal;
	font-weight: 400;
	font-size: 32px;
	line-height: 36px;
	display: flex;
	align-items: center;
	text-align: center;
	letter-spacing: 0.005em;
	color: #d5d5d5;
`;
